import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'

import Network from '@/Network'
import type {
  ListQualificationDocumentalDomainResponse,
  QualificationDocumentalDoctor,
  QualificationDocumentalPendingDoctor,
  QualificationDocumentalPolicyDetail
} from '@/Network/Types/Responses/QualificationDocumentalResponses'
import type { AvailableDoctorResponse } from '@/Network/Types/Responses/QualificationTransferResponses'
import type {
  QualificationDocumentalDoctorSearchParams,
  ToggleDoctorFieldRequest
} from '@/Network/Types/Requests/QualificationDocumentalRequests'
import type { AvailableDoctorsSearchParams } from '@/Network/Types/Requests/QualificationTransferRequests'

// 3 critérios documentais (diferente da US1, que tem 2 financeiros e resolve o label
// com ternário). O mapa cobre os três campos toggláveis da matriz de médicos.
const FIELD_LABELS: Record<ToggleDoctorFieldRequest['field'], string> = {
  hasRegistrationForm: 'Formulário de cadastro',
  hasContract: 'Contrato/Procuração',
  hasDocumentation: 'Documentação'
}

export const useQualificationDocumentalPolicyStore = defineStore(
  'qualificationDocumentalPolicy',
  () => {
    const toast = useToast()
    // Sem toast de erro aqui (rule `error-handling.md`): toda action relança o
    // ServiceError e o caller exibe via `notifyError`. O `toast.success` fica — é
    // feedback da operação concluída, idêntico em todos os callers.

    const currentPolicy = ref<QualificationDocumentalPolicyDetail | null>(null)
    const doctors = ref<QualificationDocumentalDoctor[]>([])
    const totalDoctors = ref(0)
    const isLoading = ref(false)
    const isDoctorsLoading = ref(false)
    const pendingDoctors = ref<QualificationDocumentalPendingDoctor[]>([])
    const isPendingLoading = ref(false)
    const hasLinkedSchedules = ref(false)
    const availableDoctors = ref<AvailableDoctorResponse[]>([])
    const isAvailableDoctorsLoading = ref(false)
    const availableDoctorsCount = ref(0)

    const policyName = computed(() => currentPolicy.value?.policyName ?? '')

    async function fetchPolicyById(id: number) {
      try {
        isLoading.value = true
        const response = await Network.qualificationDocumentalPolicy.getById(id)
        currentPolicy.value = response
        hasLinkedSchedules.value = response.hasLinkedSchedules ?? false
      } catch (error) {
        console.error('Erro ao carregar política documental', error)
        throw error
      } finally {
        isLoading.value = false
      }
    }

    async function fetchDoctors(
      policyId: number,
      params?: QualificationDocumentalDoctorSearchParams
    ) {
      try {
        isDoctorsLoading.value = true
        const response = await Network.qualificationDocumentalPolicy.listDoctors(policyId, params)
        doctors.value = response.data
        totalDoctors.value = response.count
      } catch (error) {
        console.error('Erro ao carregar médicos da política documental', error)
        throw error
      } finally {
        isDoctorsLoading.value = false
      }
    }

    async function fetchDoctorsBySchedule(
      scheduleId: number,
      params?: QualificationDocumentalDoctorSearchParams
    ) {
      try {
        isDoctorsLoading.value = true
        const response = await Network.qualificationDocumentalPolicy.listDoctorsBySchedule(
          scheduleId,
          params
        )
        doctors.value = response.data
        totalDoctors.value = response.count
      } catch (error) {
        console.error('Erro ao carregar médicos da escala (política documental)', error)
        throw error
      } finally {
        isDoctorsLoading.value = false
      }
    }

    async function updatePolicy(
      policyId: number,
      params: { name: string; startDate?: string; endDate?: string | null }
    ) {
      try {
        const updated = await Network.qualificationDocumentalPolicy.updatePolicy(policyId, params)
        currentPolicy.value = updated
        toast.success('Política atualizada com sucesso.')
      } catch (error) {
        console.error('Erro ao atualizar política documental', error)
        throw error
      }
    }

    async function toggleDoctorField(policyId: number, params: ToggleDoctorFieldRequest) {
      const index = doctors.value.findIndex((d) => d.id === params.doctorId)
      if (index === -1) return

      const previousValue = doctors.value[index][params.field]
      doctors.value[index] = { ...doctors.value[index], [params.field]: params.value }

      try {
        await Network.qualificationDocumentalPolicy.toggleDoctorField(policyId, params)
        const fieldLabel = FIELD_LABELS[params.field]
        const statusLabel = params.value ? 'ativado' : 'desativado'
        toast.success(`${fieldLabel} ${statusLabel} com sucesso!`)
      } catch (error) {
        console.error('Erro ao alternar critério documental do médico', error)
        const currentIndex = doctors.value.findIndex((d) => d.id === params.doctorId)
        if (currentIndex !== -1) {
          doctors.value[currentIndex] = {
            ...doctors.value[currentIndex],
            [params.field]: previousValue
          }
        }
        throw error
      }
    }

    async function addDoctors(policyId: number, doctorIds: number[]) {
      try {
        await Network.qualificationDocumentalPolicy.addDoctors(policyId, { doctorIds })
      } catch (error) {
        console.error('Erro ao adicionar médicos à política documental', error)
        throw error
      }
      // O sucesso sai antes do refresh: se só o recarregamento falhar, o usuário já
      // sabe que os médicos entraram, e o erro relançado é o da listagem.
      toast.success(
        doctorIds.length === 1
          ? 'Médico adicionado com sucesso!'
          : 'Médicos adicionados com sucesso!'
      )
      await fetchDoctors(policyId)
    }

    async function removeDoctor(policyId: number, doctorId: number) {
      const index = doctors.value.findIndex((d) => d.id === doctorId)
      if (index === -1) return

      const removed = doctors.value[index]
      doctors.value.splice(index, 1)
      totalDoctors.value--

      try {
        await Network.qualificationDocumentalPolicy.removeDoctor(policyId, doctorId)
        toast.success('Médico removido com sucesso!')
      } catch (error) {
        console.error('Erro ao remover médico da política documental', error)
        doctors.value.splice(index, 0, removed)
        totalDoctors.value++
        throw error
      }
    }

    async function fetchAvailableDoctors(policyId: number, params?: AvailableDoctorsSearchParams) {
      try {
        isAvailableDoctorsLoading.value = true
        const response = await Network.qualificationDocumentalPolicy.listAvailableDoctors(
          policyId,
          params
        )
        availableDoctors.value = response.data
        availableDoctorsCount.value = response.count
      } catch (error) {
        console.error('Erro ao carregar médicos disponíveis para a política documental', error)
        throw error
      } finally {
        isAvailableDoctorsLoading.value = false
      }
    }

    async function fetchPendingDoctors(policyId: number) {
      try {
        isPendingLoading.value = true
        const response =
          await Network.qualificationDocumentalPolicy.listPendingEvaluations(policyId)
        pendingDoctors.value = response.data
      } catch (error) {
        console.error('Erro ao carregar avaliações documentais pendentes', error)
        throw error
      } finally {
        isPendingLoading.value = false
      }
    }

    async function togglePendingDoctorField(policyId: number, params: ToggleDoctorFieldRequest) {
      const index = pendingDoctors.value.findIndex((d) => d.id === params.doctorId)
      if (index === -1) return

      const previousValue = pendingDoctors.value[index][params.field]
      pendingDoctors.value[index] = {
        ...pendingDoctors.value[index],
        [params.field]: params.value
      }

      try {
        await Network.qualificationDocumentalPolicy.toggleDoctorField(policyId, params)
        // Reusa o mapa FIELD_LABELS (3 critérios documentais) e mantém statusLabel
        // no singular, por consistência com o toggleDoctorField deste arquivo.
        const fieldLabel = FIELD_LABELS[params.field]
        const statusLabel = params.value ? 'ativado' : 'desativado'
        toast.success(`${fieldLabel} ${statusLabel} com sucesso!`)
      } catch (error) {
        console.error('Erro ao alternar critério documental do médico pendente', error)
        const currentIndex = pendingDoctors.value.findIndex((d) => d.id === params.doctorId)
        if (currentIndex !== -1) {
          pendingDoctors.value[currentIndex] = {
            ...pendingDoctors.value[currentIndex],
            [params.field]: previousValue
          }
        }
        throw error
      }
    }

    async function fetchDomain(
      policyId: number,
      domainType: 'empresa' | 'unidade' | 'escala' | 'cadeira'
    ): Promise<ListQualificationDocumentalDomainResponse> {
      try {
        const service = Network.qualificationDocumentalPolicy
        switch (domainType) {
          case 'empresa':
            return await service.listCompanyDomain(policyId)
          case 'unidade':
            return await service.listHealthUnitDomain(policyId)
          case 'escala':
            return await service.listScheduleDomain(policyId)
          case 'cadeira':
            return await service.listPositionDomain(policyId)
        }
      } catch (error) {
        console.error('Erro ao carregar vínculos da política documental', error)
        throw error
      }
    }

    function $reset() {
      currentPolicy.value = null
      doctors.value = []
      totalDoctors.value = 0
      isLoading.value = false
      isDoctorsLoading.value = false
      pendingDoctors.value = []
      isPendingLoading.value = false
      hasLinkedSchedules.value = false
      availableDoctors.value = []
      isAvailableDoctorsLoading.value = false
      availableDoctorsCount.value = 0
    }

    return {
      currentPolicy,
      doctors,
      totalDoctors,
      isLoading,
      isDoctorsLoading,
      hasLinkedSchedules,
      availableDoctors,
      isAvailableDoctorsLoading,
      availableDoctorsCount,
      policyName,
      fetchPolicyById,
      fetchDoctors,
      fetchDoctorsBySchedule,
      updatePolicy,
      toggleDoctorField,
      addDoctors,
      removeDoctor,
      fetchAvailableDoctors,
      pendingDoctors,
      isPendingLoading,
      fetchPendingDoctors,
      togglePendingDoctorField,
      fetchDomain,
      $reset
    }
  }
)
